<template>
  <Teleport to="body">
    <div v-if="visible && label" class="modal-overlay" @click.self="emit('close')">
      <div class="modal-content label-tasks-modal" @scroll="updateStickyScrollbar">
        <div class="modal-header">
          <h3 class="label-tasks-title">
            <span>{{ s__('Labels|Tasks with label') }}</span>
            <LabelBadge :label="label" />
          </h3>
          <button
            type="button"
            class="modal-close"
            :aria-label="__('Close')"
            @click="emit('close')"
          >
            ×
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loading && !hasRows" class="muted">{{ __('Loading…') }}</div>
          <div v-else-if="queryError" class="error">{{ queryError.message }}</div>
          <TaskPreviewTable
            v-else
            ref="taskTableRef"
            :rows="taskRows"
            :project-scoped="scope === 'project'"
            :grouped="scope === 'personal'"
            swimlane-mode="by_project"
            :empty-text="s__('Labels|No active tasks with this label')"
            :clickable-labels="true"
            :clickable-personal-labels="true"
            :chat-id="chatId"
            :from-id="fromId"
            @table-scroll="onTableWrapScroll"
            @label-click="emitScopedLabel('project', $event)"
            @personal-label-click="emitScopedLabel('personal', $event)"
          />

          <div v-if="pageInfo?.hasNextPage" class="label-tasks-actions mobile-stretch-btn-group">
            <button
              type="button"
              class="btn-secondary btn-sm"
              :disabled="loading"
              @click="loadMore"
            >
              <span v-if="loading">{{ __('Loading…') }}</span>
              <span v-else>{{ s__('Tasks|Show more') }}</span>
            </button>
          </div>

          <div
            v-if="showStickyScrollbar"
            ref="stickyScrollbarRef"
            class="label-tasks-sticky-h-scroll"
            @scroll="onStickyScrollbarScroll"
          >
            <div
              class="label-tasks-sticky-h-scroll-inner"
              :style="{ width: `${stickyScrollbarInnerWidth}px` }"
            ></div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { __, s__ } from '@/locale'
import { useBodyScrollLock } from '@/composables/useBodyScrollLock'
import LabelBadge from '@/components/common/LabelBadge.vue'
import TaskPreviewTable from '@/components/common/TaskPreviewTable.vue'

const props = defineProps({
  visible: { type: Boolean, default: false },
  label: { type: Object, default: null },
  scope: {
    type: String,
    default: 'project',
    validator: value => ['project', 'personal'].includes(value),
  },
  projectId: { type: [String, Number], default: null },
  chatId: { type: String, default: null },
  fromId: { type: String, default: null },
})

const emit = defineEmits(['close', 'label-click'])

const taskTableRef = ref(null)
const stickyScrollbarRef = ref(null)
const showStickyScrollbar = ref(false)
const stickyScrollbarInnerWidth = ref(0)
const syncingScroll = ref(false)

const PROJECT_LABEL_TASKS_QUERY = gql`
  query ProjectLabelTasks($projectId: ID!, $labelIds: [ID!], $after: String) {
    project(id: $projectId) {
      id
      tasks(
        labelIds: $labelIds
        includeArchived: false
        excludeFinal: true
        first: 25
        after: $after
        orderBy: "due_at"
        orderDirection: "asc"
      ) {
        edges {
          cursor
          node {
            id
            iid
            title
            priority
            dueAt
            finishedAt
            project {
              id
              title
            }
            workflowStatus {
              id
              name
              color
            }
            assignee {
              id
              username
              name
            }
            author {
              id
              username
              name
            }
            labels {
              id
              title
              color
              project {
                id
              }
            }
            personalLabels {
              id
              title
              color
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`

const PERSONAL_LABEL_TASKS_QUERY = gql`
  query PersonalLabelTasks($personalLabelIds: [ID!], $after: String) {
    currentUser {
      id
      tasks(
        personalLabelIds: $personalLabelIds
        includeArchived: false
        excludeFinal: true
        first: 25
        after: $after
        orderBy: "created_at"
        orderDirection: "desc"
      ) {
        edges {
          cursor
          node {
            id
            iid
            title
            priority
            dueAt
            finishedAt
            project {
              id
              title
            }
            workflowStatus {
              id
              name
              color
            }
            assignee {
              id
              username
              name
            }
            author {
              id
              username
              name
            }
            labels {
              id
              title
              color
              project {
                id
              }
            }
            personalLabels {
              id
              title
              color
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`

const projectQueryEnabled = computed(
  () => props.visible && props.scope === 'project' && !!props.label?.id && !!props.projectId
)
const personalQueryEnabled = computed(
  () => props.visible && props.scope === 'personal' && !!props.label?.id
)

const {
  result: projectResult,
  loading: projectLoading,
  error: projectQueryError,
  fetchMore: fetchMoreProjectTasks,
} = useQuery(
  PROJECT_LABEL_TASKS_QUERY,
  () => ({
    projectId: props.projectId ? String(props.projectId) : '',
    labelIds: props.label?.id ? [String(props.label.id)] : [],
    after: null,
  }),
  () => ({
    enabled: projectQueryEnabled.value,
    fetchPolicy: 'network-only',
  })
)

const {
  result: personalResult,
  loading: personalLoading,
  error: personalQueryError,
  fetchMore: fetchMorePersonalTasks,
} = useQuery(
  PERSONAL_LABEL_TASKS_QUERY,
  () => ({
    personalLabelIds: props.label?.id ? [String(props.label.id)] : [],
    after: null,
  }),
  () => ({
    enabled: personalQueryEnabled.value,
    fetchPolicy: 'network-only',
  })
)

useBodyScrollLock(computed(() => props.visible && !!props.label))

const loading = computed(() =>
  props.scope === 'personal' ? personalLoading.value : projectLoading.value
)
const queryError = computed(() =>
  props.scope === 'personal' ? personalQueryError.value : projectQueryError.value
)
const taskEdges = computed(() =>
  props.scope === 'personal'
    ? personalResult.value?.currentUser?.tasks?.edges || []
    : projectResult.value?.project?.tasks?.edges || []
)
const pageInfo = computed(() =>
  props.scope === 'personal'
    ? personalResult.value?.currentUser?.tasks?.pageInfo || null
    : projectResult.value?.project?.tasks?.pageInfo || null
)
const hasRows = computed(() => taskRows.value.length > 0)

const taskRows = computed(() => {
  const rows = taskEdges.value.map((edge, index) => normalizeTask(edge.node, index))
  if (props.scope !== 'personal') return rows

  return [...rows].sort((a, b) => {
    const projectCompare = a.project_title.localeCompare(b.project_title)
    if (projectCompare !== 0) return projectCompare

    return String(a.title || '').localeCompare(String(b.title || ''))
  })
})

function normalizeTask(task, index) {
  return {
    id: task.id,
    task_id: task.id,
    task_iid: task.iid,
    title: task.title,
    priority: task.priority,
    due_date: task.dueAt,
    finished_at: task.finishedAt,
    project_id: task.project?.id || props.projectId,
    project_title: task.project?.title || '',
    workflow_status_name: task.workflowStatus?.name || '',
    workflow_status_color: task.workflowStatus?.color || '',
    assignee_name: person(task.assignee),
    author_name: person(task.author),
    project_labels: (task.labels || []).map(label => ({
      ...label,
      projectId: label.project?.id || props.projectId,
    })),
    personal_labels: task.personalLabels || [],
    group_kind: props.scope === 'personal' ? 'swimlane' : undefined,
    group_label: props.scope === 'personal' ? task.project?.id : undefined,
    group_value: props.scope === 'personal' ? task.project?.title : undefined,
    __key: `label-task-${task.id || task.iid || index}`,
  }
}

function person(user) {
  if (!user) return ''
  if (user.username) return `@${user.username}`
  return user.name || ''
}

async function loadMore() {
  if (!pageInfo.value?.hasNextPage) return

  const fetchMoreTasks = props.scope === 'personal' ? fetchMorePersonalTasks : fetchMoreProjectTasks

  await fetchMoreTasks({
    variables: {
      after: pageInfo.value.endCursor,
    },
    updateQuery(previousResult, { fetchMoreResult }) {
      if (!fetchMoreResult) return previousResult

      if (props.scope === 'personal') {
        const previousEdges = previousResult?.currentUser?.tasks?.edges || []
        const nextEdges = fetchMoreResult?.currentUser?.tasks?.edges || []

        return {
          ...previousResult,
          currentUser: {
            ...previousResult.currentUser,
            tasks: {
              ...fetchMoreResult.currentUser.tasks,
              edges: [...previousEdges, ...nextEdges],
            },
          },
        }
      }

      const previousEdges = previousResult?.project?.tasks?.edges || []
      const nextEdges = fetchMoreResult?.project?.tasks?.edges || []

      return {
        ...previousResult,
        project: {
          ...previousResult.project,
          tasks: {
            ...fetchMoreResult.project.tasks,
            edges: [...previousEdges, ...nextEdges],
          },
        },
      }
    },
  })
}

function emitScopedLabel(scope, label) {
  emit('label-click', { scope, label })
}

function tableWrapElement() {
  const exposedElement = taskTableRef.value?.tableWrapElement
  return exposedElement?.value || exposedElement || null
}

function updateStickyScrollbar() {
  const wrap = tableWrapElement()
  if (!props.visible || !props.label || !wrap) {
    showStickyScrollbar.value = false
    return
  }

  const hasHorizontalOverflow = wrap.scrollWidth > wrap.clientWidth + 1

  showStickyScrollbar.value = hasHorizontalOverflow
  stickyScrollbarInnerWidth.value = wrap.scrollWidth

  if (!showStickyScrollbar.value || syncingScroll.value) return

  nextTick(() => {
    const sticky = stickyScrollbarRef.value
    if (!sticky) return
    if (Math.abs(sticky.scrollLeft - wrap.scrollLeft) < 1) return

    syncingScroll.value = true
    sticky.scrollLeft = wrap.scrollLeft
    syncingScroll.value = false
  })
}

function onTableWrapScroll() {
  const wrap = tableWrapElement()
  const sticky = stickyScrollbarRef.value
  if (!wrap || !sticky || syncingScroll.value) return

  syncingScroll.value = true
  sticky.scrollLeft = wrap.scrollLeft
  syncingScroll.value = false
}

function onStickyScrollbarScroll() {
  const wrap = tableWrapElement()
  const sticky = stickyScrollbarRef.value
  if (!wrap || !sticky || syncingScroll.value) return

  syncingScroll.value = true
  wrap.scrollLeft = sticky.scrollLeft
  syncingScroll.value = false
}

onMounted(() => {
  window.addEventListener('resize', updateStickyScrollbar)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateStickyScrollbar)
})

watch(
  () => [props.visible, props.label?.id, props.scope, taskRows.value.length, loading.value],
  async ([isVisible]) => {
    if (!isVisible) {
      showStickyScrollbar.value = false
      return
    }

    await nextTick()
    updateStickyScrollbar()
  },
  { flush: 'post' }
)
</script>

<style scoped>
.label-tasks-modal {
  max-width: min(920px, calc(100vw - 24px));
}

.label-tasks-title {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.label-tasks-actions {
  justify-content: flex-end;
  margin-top: 12px;
}

.label-tasks-sticky-h-scroll {
  position: sticky;
  bottom: 6px;
  z-index: 1;
  height: 16px;
  margin-top: 8px;
  margin-bottom: 6px;
  background: rgba(255, 255, 255, 0.12);
  overflow-x: auto;
  overflow-y: hidden;
}

.label-tasks-sticky-h-scroll-inner {
  height: 8px;
}
</style>
